import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchAll } from '../lib/fetchall'
import { useRoster } from '../lib/roster'
import { fullName, formatDateTime } from '../lib/format'
import { deleteBounce } from '../lib/bounces'
import { emptyView } from '../lib/views'
import type { Agent, Bounce } from '../lib/types'

/**
 * Every bounce, across every agent — the walkthrough-flagged 2026-08-21
 * companion to the per-agent Bounces section on `AgentPanel.tsx`. Same
 * table, same `deleteBounce`, different view: which agents need a fixed
 * address or a re-think of how they're being reached, not one agent's own
 * history.
 *
 * No report form here, deliberately — same reasoning as `Tasks.tsx`'s own
 * missing create form. A bounce is always tied to one agent (`bounces.agent_id`
 * is `not null`), and the natural place to report one is that agent's own
 * record, which already has both the one-click "Mark as bounced" on a Sent
 * row and the free-form report box.
 */
export default function Bounces() {
  const r = useRoster()
  const navigate = useNavigate()
  const [bounces, setBounces] = useState<Bounce[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<Set<number>>(new Set())

  const load = useCallback(async () => {
    // `fetchAll`, not a plain `.select('*')` — every bounce across every
    // agent, and PostgREST silently caps an unpaginated read at 1000 rows.
    // See `lib/fetchall.ts`. It only ever sorts ascending, so this orders by
    // `id` (a bigint identity — ascending id is ascending insertion order,
    // which tracks `detected_at` closely enough) and reverses once
    // in-memory, to keep the same newest-first order the direct query used
    // to give for free.
    const { rows, error } = await fetchAll<Bounce>('bounces', '*', ['id'])
    if (error) setError(error)
    else setBounces([...rows].reverse())
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (error) return <div className="empty">Could not load bounces: {error}</div>
  if (!bounces || !r.agents) return <div className="empty">Loading…</div>

  const byId = new Map(r.agents.map((a) => [a.rezen_id, a]))
  const nameOf = (agentId: string) => (byId.get(agentId) ? fullName(byId.get(agentId) as Agent) : 'Unknown agent')

  async function remove(b: Bounce) {
    setBusy((prev) => new Set(prev).add(b.id))
    try {
      const error = await deleteBounce(b.id)
      if (error) {
        setError(error)
        return
      }
      setBounces((prev) => (prev ?? []).filter((x) => x.id !== b.id))
    } finally {
      setBusy((prev) => {
        if (!prev.has(b.id)) return prev
        const next = new Set(prev)
        next.delete(b.id)
        return next
      })
    }
  }

  /** Same "show me" pattern as `Tasks.tsx`'s own `openAgent` — see the
   *  comment there for why this is a roster search-and-click rather than a
   *  deep link. */
  function openAgent(agentId: string) {
    const a = byId.get(agentId)
    if (!a) return
    const view = { ...emptyView(fullName(a)), search: a.email ?? fullName(a), include_departed: true }
    r.applyView(view)
    navigate('/')
  }

  return (
    <div className="gaps">
      <h2>Bounces</h2>
      <p className="hint">
        Every reported bounce or spam complaint, across every agent. Bulk-lane rows arrive here
        automatically off Resend; Reviewed-lane rows are reported by hand from the agent's own
        record, since this dashboard cannot see inside Gmail.
      </p>

      <div className="toolbar" style={{ padding: '10px 0' }}>
        <span className="cnt">{bounces.length} reported</span>
      </div>

      {bounces.length === 0 && <div className="hint">None reported.</div>}

      {bounces.map((b) => (
        <div className="entry" key={b.id}>
          <div className="emeta">
            <button className="btn ghost sm" onClick={() => openAgent(b.agent_id)} style={{ textAlign: 'left' }}>
              {nameOf(b.agent_id)}
            </button>
            <span className="pill s-failed">{b.kind}</span>
          </div>
          <div className="dsubject">{b.address}</div>
          {b.subject && <div className="hint">{b.subject}</div>}
          {b.reason && <div className="hint">{b.reason}</div>}
          <div className="bounce-foot">
            <span className="hint">
              {b.source === 'resend' ? 'Reported automatically' : 'Reported by hand'} ·{' '}
              {formatDateTime(b.detected_at)}
            </span>
            <button
              className="btn ghost sm"
              onClick={() => void remove(b)}
              disabled={busy.has(b.id)}
              aria-label={`Delete bounce report for ${b.address}`}
            >
              Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
